import React, { useEffect, useState } from 'react';
import PrinterSelect from './printing/PrinterSelect';
import { useToast } from '../contexts/ToastContext';
import { usePrintDefaults } from '../hooks/usePrintDefaults';
import printingService, { printErrorMessage, type PrinterType } from '../services/printing.service';

/**
 * The "Printing" section of the user settings drawer: default laser and label
 * printer, the 360 equivalent of V1's gwi.users.default_printer /
 * default_label. Each change saves immediately (one PUT per pick) and the
 * shared defaults cache is refreshed so any open Print dialog preselects the
 * new printer without a reload.
 */
const PrintPreferencesSection: React.FC = () => {
  const { showToast } = useToast();
  const { defaults, refresh } = usePrintDefaults();
  const [laser, setLaser] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState<PrinterType | null>(null);

  useEffect(() => {
    setLaser(defaults.laser ?? '');
    setLabel(defaults.label ?? '');
  }, [defaults]);

  const save = async (type: PrinterType, value: string) => {
    const key = type === 'LASER' ? 'laser' : 'label';
    const previous = type === 'LASER' ? laser : label;
    (type === 'LASER' ? setLaser : setLabel)(value);
    setSaving(type);
    try {
      await printingService.setDefaults({ [key]: value });
      await refresh();
      showToast({
        type: 'success',
        message: value ? `Default ${type.toLowerCase()} printer saved` : `Default ${type.toLowerCase()} printer cleared`,
      });
    } catch (err) {
      (type === 'LASER' ? setLaser : setLabel)(previous);
      showToast({ type: 'error', message: printErrorMessage(err) });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-theme-border-primary space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-heading">Printing</h4>
        <p className="text-xs text-muted mt-0.5">
          Where Print sends your documents and labels unless you pick another printer in the
          Print dialog. Printers are managed under Table Maintenance › Printers.
        </p>
      </div>

      <div>
        <label className="text-sm text-label block mb-1">Default Laser Printer</label>
        <PrinterSelect
          type="LASER"
          value={laser}
          onChange={(v) => save('LASER', v)}
          disabled={saving === 'LASER'}
          showClearOption
          ariaLabel="Default Laser Printer"
        />
      </div>

      <div>
        <label className="text-sm text-label block mb-1">Default Label Printer</label>
        <PrinterSelect
          type="LABEL"
          value={label}
          onChange={(v) => save('LABEL', v)}
          disabled={saving === 'LABEL'}
          showClearOption
          ariaLabel="Default Label Printer"
        />
      </div>
    </div>
  );
};

export default PrintPreferencesSection;
